import { createAction } from '@reduxjs/toolkit'
import { l } from '@manychat/manyui'

import * as actionTypes from 'apps/applications/constants/ReduxActionTypes'
import {
  getReviewReportApplicationId,
  getReviewVersion,
} from 'apps/applications/selectors/applicationsSelectors'
import { alert } from 'common/core'
import { IThunkAction } from 'common/core/interfaces/actions'
import { ApplicationsApi } from 'shared/api/requests/applications'
import { ConfigError } from 'shared/api/requests/applications/errors'
import { DevApplication } from 'shared/api/requests/applications/schemas'
import { InvalidErrorResponseError } from 'shared/errors/protocolLayerError'
import { createAsyncAction } from 'shared/lib/redux'
import { typedRouting } from 'utils/router'
import { navigatePollyfill } from 'utils/router/tools'

// Temporary solution for old API errors response structure
// TC-1644 Remove legacy error format handling for applications endpoints
const getErrors = (error: unknown) => {
  if (error instanceof InvalidErrorResponseError) {
    if (
      error.response?.data &&
      typeof error.response?.data === 'object' &&
      'errors' in error.response.data &&
      Array.isArray(error.response.data.errors)
    ) {
      return error.response.data.errors as ConfigError[]
    }
  }

  return []
}

export const createApplication = createAsyncAction(
  'applications/createApplication',
  async (draft: DevApplication) => {
    const { data } = await ApplicationsApi.create({ body: draft })

    navigatePollyfill(typedRouting.linkBuilder.profileDashboard({ hash: 'applications' }), {
      replace: true,
    })
    alert(l.translate('Great work! The application was successfully created'), 'success')

    return data.app
  },
  {
    onError: (error, _, { rejectWithValue }) => {
      const errors = getErrors(error)
      const configErrors = errors.reduce<ConfigError[]>((result, error) => {
        if (ApplicationsApi.create.isConfigError(error)) {
          result.push(error)
        }

        return result
      }, [])

      if (configErrors.length > 0) {
        alert(
          l.translate('The App could not be created because of errors in the configuration'),
          'danger',
        )

        throw rejectWithValue({
          field: 'config_errors',
          error_data: { configErrors },
          message: configErrors[0].message,
          original_message: configErrors[0].message,
        })
      }
    },
  },
)

export const updateApplication = createAsyncAction(
  'applications/updateApplication',
  async ({ data: draft, app_id }: { data: DevApplication; app_id: number }) => {
    const { data } = await ApplicationsApi.update({ body: { ...draft, app_id } })

    alert(l.translate('Your application successfully updated!'), 'success')

    return data.app
  },
  {
    onError: (error, _, { rejectWithValue }) => {
      const errors = getErrors(error)
      const configErrors = errors.reduce<ConfigError[]>((result, error) => {
        if (ApplicationsApi.update.isConfigError(error)) {
          result.push(error)
        }

        return result
      }, [])

      if (configErrors.length > 0) {
        alert(
          l.translate('The App could not be updated because of errors in the configuration'),
          'danger',
        )

        throw rejectWithValue({
          field: 'config_errors',
          error_data: { configErrors },
          message: configErrors[0].message,
          original_message: configErrors[0].message,
        })
      }
    },
  },
)

export const cleanErrors = createAction('applications/cleanErrors')

export const fetchApplication = createAsyncAction(
  'applications/fetchApplication',
  async (applicationId: number) => {
    const { data } = await ApplicationsApi.fetchItem({ query: { app_id: applicationId } })
    return data.app
  },
)

// Publish

export const openReviewReportModal =
  (id: number): IThunkAction =>
  (dispatch) =>
    dispatch({ type: actionTypes.APPLICATION_REVIEW_REPORT_MODAL_OPEN, applicationId: id })

export const closeReviewReportModal = (): IThunkAction => (dispatch) =>
  dispatch({ type: actionTypes.APPLICATION_REVIEW_REPORT_MODAL_CLOSE })

export const fetchReviewReport = createAsyncAction(
  'applications/fetchReviewReport',
  async (_, { getState, rejectWithValue }) => {
    const state = getState()
    const applicationId = getReviewReportApplicationId(state)
    if (applicationId === null) {
      throw rejectWithValue()
    }
    const version = getReviewVersion(applicationId)(state)?.version

    if (!version) {
      throw rejectWithValue()
    }

    const { data } = await ApplicationsApi.getReport({
      query: { app_id: applicationId, version },
    })

    return data.report
  },
)

export const uploadApplicationAvatar = createAsyncAction(
  'applications/uploadApplicationAvatar',
  async (avatar: Blob) => {
    const formData = new FormData()
    formData.append('avatar', avatar)
    const { data } = await ApplicationsApi.uploadAvatar({ body: formData })

    return data.avatar
  },
)
